import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2, Heart, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { preInscricoesApi } from '../lib/api';
import logger from '../lib/logger';

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];
const COMO_CONHECEU = ['Instagram', 'Facebook', 'YouTube', 'WhatsApp', 'Indicação de amigos', 'Igreja', 'Participei de outra edição', 'Google', 'Outro'];

export const maskPhone = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const getUtm = () => {
  const p = new URLSearchParams(window.location.search);
  const utm = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((k) => { if (p.get(k)) utm[k] = p.get(k); });
  return Object.keys(utm).length ? utm : undefined;
};

const initial = { nome_participante_1: '', nome_participante_2: '', telefone: '', email: '', cidade: '', estado: '', ja_participou: '', como_conheceu: '', aceite_privacidade: false, aceite_comunicacoes: false, website: '' };

const validate = (f) => {
  const e = {};
  if (f.nome_participante_1.trim().length < 3) e.nome_participante_1 = 'Informe o nome completo';
  if (f.nome_participante_2.trim().length < 3) e.nome_participante_2 = 'Informe o nome completo';
  const digits = f.telefone.replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 11) e.telefone = 'Informe um WhatsApp válido com DDD';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) e.email = 'Informe um e-mail válido';
  if (f.cidade.trim().length < 3) e.cidade = 'Informe a cidade';
  if (!f.estado) e.estado = 'Selecione o estado';
  if (f.ja_participou === '') e.ja_participou = 'Selecione uma opção';
  if (!f.aceite_privacidade) e.aceite_privacidade = 'É necessário aceitar a Política de Privacidade';
  return e;
};

const Field = ({ label, error, children, required = true }) => (
  <div>
    <label className="block text-gray-300 text-sm mb-1.5">{label}{required && <span className="text-kairos-orange"> *</span>}</label>
    {children}
    {error && <p className="text-red-400 text-xs mt-1 flex items-center gap-1" role="alert"><AlertCircle className="w-3 h-3" />{error}</p>}
  </div>
);

const inputCls = 'bg-obsidian-50 border-white/10 text-white placeholder:text-gray-600 h-12 focus-visible:ring-kairos-orange';
const selectCls = `${inputCls} w-full rounded-md border px-3 text-sm focus:outline-none`;

export const PreInscricaoSuccess = ({ periodo, atualizada }) => (
  <div className="text-center py-6" data-testid="pre-inscricao-sucesso">
    <div className="w-20 h-20 rounded-full bg-green-500/15 border border-green-500/40 flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-10 h-10 text-green-400" /></div>
    <h3 className="font-display text-2xl md:text-3xl text-white mb-4">{atualizada ? 'PRÉ-INSCRIÇÃO ATUALIZADA!' : 'PRÉ-INSCRIÇÃO RECEBIDA COM SUCESSO!'}</h3>
    <div className="text-gray-300 leading-relaxed space-y-3 max-w-xl mx-auto text-sm md:text-base">
      <p>Que alegria saber que vocês desejam participar do Kairós {periodo.slice(-4)}!</p>
      <p>Recebemos os dados de vocês. Em breve, divulgaremos a abertura do primeiro lote, os valores e todas as informações necessárias.</p>
      <p className="text-white font-medium">Data do próximo encontro: {periodo}.</p>
      <p>Continuem acompanhando nossas novidades!</p>
    </div>
    <p className="text-gray-500 text-xs mt-6 max-w-xl mx-auto">Atenção: a pré-inscrição representa uma manifestação de interesse e não garante vaga, reserva de hospedagem, preço ou participação confirmada.</p>
    <Link to="/" className="inline-block mt-8"><Button variant="outline" className="rounded-full border-white/20 text-white hover:bg-white/5">Voltar para o início</Button></Link>
  </div>
);

export const PreInscricaoForm = ({ periodo, compact = false }) => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [serverError, setServerError] = useState('');

  const set = (k, v) => { setForm((p) => ({ ...p, [k]: v })); if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined })); };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    setServerError('');
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      const res = await preInscricoesApi.create({
        ...form,
        email: form.email.trim().toLowerCase(),
        ja_participou: form.ja_participou === 'sim',
        como_conheceu: form.como_conheceu || null,
        origem: 'site',
        utm: getUtm(),
      });
      setDone({ atualizada: res.data.atualizada });
    } catch (err) {
      logger.error('Erro pré-inscrição:', err);
      const detail = err.response?.data?.detail;
      setServerError(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map((d) => d.msg?.replace('Value error, ', '')).join(' • ') : 'Não foi possível enviar. Tente novamente.');
    } finally {
      setSending(false);
    }
  };

  if (done) return <PreInscricaoSuccess periodo={periodo} atualizada={done.atualizada} />;

  return (
    <form onSubmit={submit} noValidate className="space-y-5" data-testid="pre-inscricao-form">
      <input type="text" name="website" value={form.website} onChange={(e) => set('website', e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      <div className="flex items-center gap-2 text-kairos-orange text-xs font-semibold tracking-[0.2em]"><Heart className="w-4 h-4" /> DADOS DO CASAL</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Nome completo do 1º participante" error={errors.nome_participante_1}>
          <Input value={form.nome_participante_1} onChange={(e) => set('nome_participante_1', e.target.value)} placeholder="Nome completo" className={inputCls} data-testid="pi-nome1" autoComplete="name" />
        </Field>
        <Field label="Nome completo do 2º participante" error={errors.nome_participante_2}>
          <Input value={form.nome_participante_2} onChange={(e) => set('nome_participante_2', e.target.value)} placeholder="Nome completo" className={inputCls} data-testid="pi-nome2" />
        </Field>
        <Field label="WhatsApp principal (com DDD)" error={errors.telefone}>
          <Input value={form.telefone} onChange={(e) => set('telefone', maskPhone(e.target.value))} placeholder="(69) 99999-9999" inputMode="tel" className={inputCls} data-testid="pi-telefone" autoComplete="tel" />
        </Field>
        <Field label="E-mail principal" error={errors.email}>
          <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="casal@email.com" className={inputCls} data-testid="pi-email" autoComplete="email" />
        </Field>
        <Field label="Cidade" error={errors.cidade}>
          <Input value={form.cidade} onChange={(e) => set('cidade', e.target.value)} placeholder="Sua cidade" className={inputCls} data-testid="pi-cidade" />
        </Field>
        <Field label="Estado" error={errors.estado}>
          <select value={form.estado} onChange={(e) => set('estado', e.target.value)} className={selectCls} data-testid="pi-estado">
            <option value="">Selecione</option>
            {UFS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
          </select>
        </Field>
        <Field label="Já participaram do Kairós anteriormente?" error={errors.ja_participou}>
          <div className="flex gap-3" role="radiogroup">
            {[['sim', 'Sim'], ['nao', 'Não']].map(([v, l]) => (
              <button type="button" key={v} onClick={() => set('ja_participou', v)} data-testid={`pi-ja-participou-${v}`} aria-pressed={form.ja_participou === v}
                className={`flex-1 h-12 rounded-md border text-sm transition-colors ${form.ja_participou === v ? 'bg-kairos-orange/20 border-kairos-orange text-white' : 'bg-obsidian-50 border-white/10 text-gray-400 hover:border-white/30'}`}>{l}</button>
            ))}
          </div>
        </Field>
        <Field label="Como conheceram o Kairós?" required={false}>
          <select value={form.como_conheceu} onChange={(e) => set('como_conheceu', e.target.value)} className={selectCls} data-testid="pi-como-conheceu">
            <option value="">Opcional</option>
            {COMO_CONHECEU.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
      </div>

      <div className="space-y-3 pt-2">
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox checked={form.aceite_privacidade} onCheckedChange={(v) => set('aceite_privacidade', !!v)} className="mt-0.5 border-white/30 data-[state=checked]:bg-kairos-orange data-[state=checked]:border-kairos-orange" data-testid="pi-aceite-privacidade" />
          <span className="text-gray-300 text-sm leading-relaxed">Li e aceito a <Link to="/politica-de-privacidade" target="_blank" className="text-kairos-orange underline">Política de Privacidade</Link> e autorizo o uso dos dados para administração da pré-inscrição. <span className="text-kairos-orange">*</span></span>
        </label>
        {errors.aceite_privacidade && <p className="text-red-400 text-xs flex items-center gap-1" role="alert"><AlertCircle className="w-3 h-3" />{errors.aceite_privacidade}</p>}
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox checked={form.aceite_comunicacoes} onCheckedChange={(v) => set('aceite_comunicacoes', !!v)} className="mt-0.5 border-white/30 data-[state=checked]:bg-kairos-orange data-[state=checked]:border-kairos-orange" data-testid="pi-aceite-comunicacoes" />
          <span className="text-gray-400 text-sm leading-relaxed">Quero receber novidades e comunicações promocionais sobre o Kairós (opcional).</span>
        </label>
      </div>

      <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4 text-gray-400 text-xs leading-relaxed" data-testid="pi-aviso-interesse">
        <strong className="text-gray-300">Atenção:</strong> a pré-inscrição representa uma manifestação de interesse e não garante vaga, reserva de hospedagem, preço ou participação confirmada.
      </div>

      {serverError && <div className="rounded-xl bg-red-500/10 border border-red-500/30 p-4 text-red-300 text-sm flex items-start gap-2" role="alert" data-testid="pi-erro-servidor"><AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />{serverError}</div>}

      <Button type="submit" disabled={sending} data-testid="pi-submit"
        className={`w-full ${compact ? 'h-12' : 'h-14'} rounded-full bg-gradient-to-r from-kairos-orange to-kairos-orange-light text-white font-semibold text-base shadow-[0_0_30px_rgba(255,107,0,0.3)] hover:shadow-[0_0_50px_rgba(255,107,0,0.5)] transition-all disabled:opacity-70`}>
        {sending ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Enviando...</> : <>CONFIRMAR NOSSA PRÉ-INSCRIÇÃO<ArrowRight className="w-5 h-5 ml-2" /></>}
      </Button>
    </form>
  );
};
